import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';

import {
  ClinicalTrialsService,
  ICreateExportOptions,
  IOption,
} from '@app/core/clinical-trials';
import {
  ConditionBuilderConfig,
  FieldMap,
  Option,
  RuleSet,
} from '@app/shared/condition-builder';
import { EntitiesService, IEntity } from '@app/core/entities';
import { ToastrService } from 'ngx-toastr';
import { ICreateUpdateQueryRequest, QueryService } from '@app/core/queries';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AuthService } from '@app/core/auth';
import { rbac } from '@app/shared/rbac';
import {
  fieldOptions,
  genderOptions,
  IOptionWithDemographic,
  LABORATORY_TYPES,
  laboratoryTypes,
  urgenciesOptions,
} from './estudios-clinicos.options';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import {
  CsvLoadErrorItem,
  CsvLoadSuccess,
} from '@app/core/csv-load/csv-load.service';

@Component({
  selector: 'app-query-basic',
  templateUrl: './query-basic.component.html',
  styleUrls: ['./query-basic.component.css'],
  providers: [ConfirmationService],
})
export class QueryBasicComponent implements OnInit, OnDestroy {
  public centers$: Observable<IOption[]>;
  public labs$: Observable<IOption[]>;
  public areas$: Observable<IOption[]>;
  public antibioticos$: Observable<IOption[]>;
  public services$: Observable<IOption[]>;
  public diagnosis$: Observable<IOption[]>;
  public targets$: Observable<IOption[]>;
  public tiposPeticion$: Observable<IOption[]>;
  public genders: IOption[];
  public urgencies: IOption[];
  public config$: Subject<ConditionBuilderConfig>;
  public form: FormGroup;
  public ruleset: RuleSet;
  public queryName: string;
  private errors: FormError[] = [];
  private _fieldOptionsWithDemographic: IOptionWithDemographic[] = fieldOptions;
  private _oldSelectedFields: string[] = [];

  private entities: IEntity[];

  public methods = Array<{ label: string; value: number }>();

  public materials = Array<{ label: string; value: number }>();

  private savedQuery$: Subject<any> = new Subject();

  public ruleset$: Subject<RuleSet> = new Subject();

  public selectedLabType: LABORATORY_TYPES = LABORATORY_TYPES.General;

  public laboratoryTypes = laboratoryTypes;

  public demographicDataOnly: boolean = false;

  public includeHistoricalData: boolean = false;
  public loadedNhcCount = 0;
  public loadedPeticionesCount = 0;

  public showHistoricalDataCheckbox: boolean = false;

  public historicDatabaseName: string;

  private destroyed$: Subject<void>;

  constructor(
    private entityService: EntitiesService,
    private service: ClinicalTrialsService,
    private toastr: ToastrService,
    private queryService: QueryService,
    private route: ActivatedRoute,
    private _authService: AuthService,
    private _router: Router,
    private _location: Location,
    _translate: TranslateService
  ) {
    this.destroyed$ = new Subject();
    this.config$ = new Subject();

    this.genders = genderOptions.map((option) => ({
      id: option.id,
      description: _translate.instant(option.description),
    }));

    this.urgencies = urgenciesOptions.map((option) => ({
      id: option.id,
      description: _translate.instant(option.description),
    }));

    this._fieldOptionsWithDemographic = fieldOptions.map((option) => ({
      id: option.id,
      description: _translate.instant(option.description),
      demographic: option.demographic,
    }));

    this.form = new FormGroup({
      centerId: new FormControl(),
      serviceId: new FormControl(),
      targetId: new FormControl(),
      diagnosisId: new FormControl(),
      areaId: new FormControl(),
      labId: new FormControl(),
      tipoPeticionId: new FormControl(),
      registerRange: new FormControl(),
      ageFrom: new FormControl(),
      ageTo: new FormControl(),
      validationRange: new FormControl(),
      gender: new FormControl(),
      reasonRequest: new FormControl(),
      urgent: new FormControl(),
      comment: new FormControl(),
      validationRangeEpisode: new FormControl(),
      episode: new FormControl(),
      priority: new FormControl(),
      target: new FormControl(),
      externalSystem: new FormControl(),
      externalDate: new FormControl(),
      info9: new FormControl(),
      notesHCIS: new FormControl(),
      extractor: new FormControl(),
      appointments: new FormControl(),
      appointmentDates: new FormControl(),
      hcisUnity: new FormControl(),
      methods: new FormControl([]),
      strictMethodMatch: new FormControl(false),
      fields: new FormControl([]),
      numPeticion: new FormControl([]),
      codPatient: new FormControl([]),
    });

    this.centers$ = this.service.getCenters();
    this.labs$ = this.service.getLabs();
    this.areas$ = this.service.getAreas();
    this.antibioticos$ = this.service.getAntibioticos();
    this.services$ = this.service.getServices();
    this.diagnosis$ = this.service.getDiagnosis();
    this.targets$ = this.service.getTargets();
    this.tiposPeticion$ = this.service.getTiposPeticion();

    this.form
      .get('methods')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.syncStrictMethodMatchState());
    this.syncStrictMethodMatchState();
  }

  private syncStrictMethodMatchState(): void {
    const methodsControl = this.form.get('methods');
    const strictControl = this.form.get('strictMethodMatch');
    if (!methodsControl || !strictControl) {
      return;
    }

    const selectedMethods = methodsControl.value as unknown[];
    const hasEnoughMethods =
      Array.isArray(selectedMethods) && selectedMethods.length > 1;
    const canUseStrictMatch = this.isGeneralLab && hasEnoughMethods;

    if (!canUseStrictMatch) {
      strictControl.setValue(false, { emitEvent: false });
      strictControl.disable({ emitEvent: false });
      return;
    }

    strictControl.enable({ emitEvent: false });
  }

  private loadQueryBuilderConfig(): void {
    this.service
      .getQueryBuilderConfig()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (config) => {
          if (config && config.historicDatabaseName) {
            this.historicDatabaseName = config.historicDatabaseName;
            this.showHistoricalDataCheckbox = true;
          } else {
            this.showHistoricalDataCheckbox = false;
          }
        },
        error: (error) => {
          // Si el endpoint devuelve 404 o error, ocultar el checkbox
          this.showHistoricalDataCheckbox = false;
        },
      });
  }

  public ngOnInit(): void {
    // Cargar configuración del query builder (para mostrar checkbox de datos históricos)
    this.loadQueryBuilderConfig();

    // check permissions
    const permissionGeneral = this._authService.hasProfile(
      rbac.profiles.QUERY_EXPORT_CRUD_GENERAL
    );
    const permissionLab = this._authService.hasProfile(
      rbac.profiles.QUERY_EXPORT_CRUD_MICRO
    );

    if (!permissionGeneral) {
      this.selectedLabType = LABORATORY_TYPES.Micro;
      this.laboratoryTypes = this.laboratoryTypes.filter(
        (type) => type.id === LABORATORY_TYPES.Micro
      );
    }

    if (!permissionLab) {
      this.selectedLabType = LABORATORY_TYPES.General;
      this.laboratoryTypes = this.laboratoryTypes.filter(
        (type) => type.id === LABORATORY_TYPES.General
      );
    }

    const id = this.route.snapshot.params['id'];

    forkJoin([
      id ? this.queryService.getQueryById(id) : of(null),
      this.entityService.getEntities(),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((values) => {
        const [query, entities] = values;

        this.entities = entities;

        this.materials = entities
          .find((e) => e.title === 'material')
          .fields.map((f) => ({
            label: f.title,
            value: f.id,
          }));

        if (query) {
          this.queryName = query.title;
          this.selectedLabType = query.labType as LABORATORY_TYPES;
          this.includeHistoricalData = !!query.historicDatabaseName;
          const _parsedData = JSON.parse(query.data);
          const _ruleset = this.isGeneralLab
            ? this.changeRulesetFieldTitle(_parsedData.queryBuilder)
            : _parsedData.queryBuilder;
          this.ruleset = _ruleset;

          const _formData = this.convertAllRangesToDate(_parsedData);

          this.form.patchValue(_formData);
          this.demographicDataOnly = _parsedData.demographicDataOnly || false;
        }

        this.syncStrictMethodMatchState();
        this.loadQueryConfig();
      });

    this.config$.pipe(takeUntil(this.destroyed$)).subscribe((config) => {
      console.log('config changed', config);
      this.mapFieldMapToMethods(config.fields);
    });
  }

  private mapFieldMapToMethods(methodMap: FieldMap) {
    this.methods = Object.keys(methodMap).map((label) => {
      return {
        label,
        value: methodMap[label].extraValue,
      };
    });
  }

  private convertAllRangesToDate(queryParsedData): any {
    const ranges = [
      'registerRange',
      'validationRange',
      'validationRangeEpisode',
    ];
    ranges.forEach((range) => {
      if (queryParsedData[range]) {
        queryParsedData[range] = this.createDatesFromRange(
          queryParsedData[range]
        );
      }
    });
    return queryParsedData;
  }

  private createDatesFromRange(
    range: [string, string]
  ): [Date, Date] | undefined {
    if (!range) {
      return undefined;
    }
    const [from, to] = range;
    return [new Date(from), new Date(to)];
  }

  /**
   * getter fieldOptions
   * - Marcado: solo campos demográficos
   * - Desmarcado: todos los campos
   */
  public get fieldOptions(): IOptionWithDemographic[] {
    if (this.demographicDataOnly) {
      return this._fieldOptionsWithDemographic.filter((option) => option.demographic);
    }
    return this._fieldOptionsWithDemographic;
  }

  public validate(onSave: boolean = false): boolean {
    // for save, we need to check if queryName is set
    if (onSave) {
      if (!this.queryName) {
        this.errors.push({
          field: 'queryName',
          message: 'El nombre de la consulta es obligatorio',
        });
        this.toastr.error('El nombre de la consulta es obligatorio');
        return false;
      }
    }
    return true;
  }

  onDemographicDataOnlyChanged(): void {
    if (this.demographicDataOnly) {
      this._oldSelectedFields = this.form.value.fields;
      this.form.patchValue({
        fields: this.fieldOptions.map((option) => option.id),
      });
    } else {
      this.form.patchValue({
        fields: this._oldSelectedFields,
      });
    }
  }

  private changeRulesetFieldTitle(originalRuleset: RuleSet): RuleSet {
    // copy object
    const ruleset = JSON.parse(JSON.stringify(originalRuleset));

    ruleset.rules = ruleset.rules.map((rule) => {
      if (rule.condition) {
        return this.changeRulesetFieldTitle(rule);
      } else {
        rule.field = this.entities[0].fields.find(
          (entity) => entity.id === rule.field
        )?.title;
        return rule;
      }
    });
    return ruleset;
  }

  private changeRulesetFieldId(originalRuleset: RuleSet): void {
    // copy object
    const ruleset = JSON.parse(JSON.stringify(originalRuleset));

    ruleset.rules = ruleset.rules.map((rule) => {
      if (rule.condition) {
        return this.changeRulesetFieldId(rule);
      } else {
        rule.field = this.entities[0].fields.find(
          (entity) => entity.title?.toLowerCase() === rule.field?.toLowerCase()
        ).id;
        return rule;
      }
    });
    return ruleset;
  }

  public getErrorMessage(field: string): string {
    return this.errors.find((error) => error.field === field)?.message;
  }

  public save(): void {
    const query = this.createFullRuleset();
    const id = this.route.snapshot.params['id'];

    if (!this.validate(true)) {
      return;
    }
    const queryData: ICreateUpdateQueryRequest = {
      title: this.queryName,
      data: JSON.stringify(query),
      labType: this.selectedLabType,
    };
    if (this.includeHistoricalData) {
      queryData.historicDatabaseName = this.historicDatabaseName;
    }
    if (id) {
      queryData.id = id;
    }

    this.queryService
      .createUpdateQuery(queryData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.savedQuery$.next(res);
          this.toastr.success('Query saved');
        },
        error: (error) => {
          this.toastr.error('Error saving query');
        },
      });
  }

  public onSelectedLabTypeChanged(): void {
    this.syncStrictMethodMatchState();
    this.loadQueryConfig();
  }

  get isGeneralLab(): boolean {
    return this.selectedLabType === LABORATORY_TYPES.General;
  }

  get isMicroLab(): boolean {
    return this.selectedLabType === LABORATORY_TYPES.Micro;
  }

  private loadQueryConfig(): void {
    const config: ConditionBuilderConfig = {
      entities: {},
      fields: {},
    };

    if (this.selectedLabType === LABORATORY_TYPES.General) {
      if (!this.entities) {
        return;
      }

      this.entities
        .filter((entity) => entity.id === 5)
        .forEach((entity) => {
          config.entities[entity.title] = {
            name: entity.title,
            value: entity.title,
          };

          entity.fields.forEach((field) => {
            config.fields[field.title] = {
              name: field.title,
              value: field.title,
              extraValue: field.id,
              type: field.fieldType,
              entity: entity.title,
              operators: ['=', '>', '<', '>=', '<=', '!=', 'CONTIENE'],
            };
          });
        });
    } else if (this.selectedLabType === LABORATORY_TYPES.Micro) {
      config.entities['microorganismo'] = {
        name: 'filter',
        value: 'filter',
      };

      if (!this.entities) {
        return;
      }

      const antibioticosOptions: Option[] =
        this.entities
          .find((e) => e.title === 'antibiotico')
          ?.fields.map((field) => ({
            name: field.title,
            value: field.id,
          })) || [];

      const sensibilidadesOptions: Option[] = this.entities
        .find((e) => e.title === 'sensibilidad')
        ?.fields.map((field) => ({
          name: field.title,
          value: field.id,
        }));
      console.log({ sensibilidadesOptions });

      const microorganismosOptions: Option[] = this.entities
        .find((e) => e.title === 'microorganismo')
        ?.fields.map((field) => ({
          name: field.title,
          value: field.id,
        }));
      console.log({ microorganismosOptions });

      const fieldsForMicro: FieldMap = {
        Microorganismo: {
          name: 'Microorganismo',
          value: 'Microorganismo',
          type: 'multiselect',
          operators: ['IN'],
          options: microorganismosOptions,
        },
        Sensibilidad: {
          name: 'Sensibilidad',
          value: 'Sensibilidad',
          type: 'multiselect',
          operators: ['IN'],
          options: sensibilidadesOptions,
        },
        Antibiotico: {
          name: 'Antibiotico',
          value: 'Antibiotico',
          type: 'multiselect',
          operators: ['IN'],
          options: antibioticosOptions,
        },
        CMI: {
          name: 'CMI',
          value: 'CMI',
          type: 'string',
          operators: ['CONTIENE'],
        },
      };

      this.entities
        .filter((entity) => entity.id === 6)
        .forEach((entity) => {
          config.entities[entity.title] = {
            name: entity.title,
            value: entity.title,
          };

          config.fields = fieldsForMicro;
        });
    }

    this.config$.next(config);
  }

  private removeEmptyRules(ruleset: RuleSet): RuleSet {
    if (!ruleset?.rules) {
      return ruleset;
    }

    const cleanedRules = ruleset.rules
      .map((item: any) => {
        // Si es un RuleSet (tiene rules), limpiar recursivamente
        if (Array.isArray(item.rules)) {
          const cleanedSubRuleset = this.removeEmptyRules(item);
          // Si después de limpiar el sub-ruleset queda vacío, eliminarlo
          if (!cleanedSubRuleset.rules?.length) {
            return null;
          }
          return cleanedSubRuleset;
        }
        // Si es una Rule, verificar si está vacía
        else if (item.field && item.operator) {
          // Una regla está vacía si tiene operador pero no tiene valor (o el valor está vacío)
          const isEmpty =
            item.value === null ||
            item.value === undefined ||
            item.value === '' ||
            (Array.isArray(item.value) && item.value.length === 0);

          if (isEmpty) {
            return null; // Eliminar esta regla
          }
          return item; // Mantener esta regla
        }
        // Si no tiene field u operator, es inválida, eliminarla
        return null;
      })
      .filter((item: any) => item !== null); // Filtrar las reglas eliminadas

    return {
      ...ruleset,
      rules: cleanedRules,
    };
  }

  private createFullRuleset(): ICreateExportOptions {
    if (this.ruleset) {
      // Limpiar reglas vacías antes de procesar
      const cleanedRuleset = this.removeEmptyRules(this.ruleset);
      const queryBuilder = this.isGeneralLab
        ? this.changeRulesetFieldId(cleanedRuleset)
        : cleanedRuleset;
      return {
        ...this.form.value,
        queryBuilder,
        demographicDataOnly: this.demographicDataOnly,
      };
    } else {
      return {
        ...this.form.value,
        demographicDataOnly: this.demographicDataOnly,
      };
    }
  }

  public submit(): void {
    this.service
      .createExport(this.createFullRuleset(), this.includeHistoricalData ? this.historicDatabaseName : undefined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastr.success(
          'Exportación en curso, puede consultar el estado en Export Results'
        );
      });

    if (!this.validate()) {
      this.toastr.error('Please fill all the required values in conditions.');
    }
  }

  public onNhcLoaded(result: CsvLoadSuccess): void {
    this.form.get('codPatient').setValue(result.values);
    this.loadedNhcCount = result.total_cargados;
  }

  public onPeticionesLoaded(result: CsvLoadSuccess): void {
    this.form.get('numPeticion').setValue(result.values);
    this.loadedPeticionesCount = result.total_cargados;
  }

  public onNhcLoadError(_errors: CsvLoadErrorItem[]): void {}

  public onPeticionesLoadError(_errors: CsvLoadErrorItem[]): void {}

  public clearNhcValues(): void {
    this.form.get('codPatient').setValue([]);
    this.loadedNhcCount = 0;
  }

  public clearPeticionesValues(): void {
    this.form.get('numPeticion').setValue([]);
    this.loadedPeticionesCount = 0;
  }

  public showResults(): void {
    this.savedQuery$.pipe(takeUntil(this.destroyed$)).subscribe((query) => {
      console.log('query****', query);
      const currentId = this.route.snapshot.params['id'];
      // Si es una consulta nueva (sin ID), actualizar la URL en el historial antes de navegar a resultados
      // Esto permite que al volver atrás desde resultados, se vuelva a la edición de la consulta
      if (!currentId && query.id) {
        this._location.replaceState(`/query/${query.id}`);
      }
      this._router.navigate(['/query/results', query.id]);
    });
    this.save();
  }

  public conditionChanged(event: RuleSet): void {
    console.log('condition changed', event);
    this.ruleset$.next(event);
  }

  get showCrearExport(): boolean {
    return (
      this._authService.hasProfile(rbac.profiles.QUERY_EXPORT_CRUD_GENERAL) ||
      this._authService.hasProfile(rbac.profiles.QUERY_EXPORT_CRUD_MICRO)
    );
  }

  get showQuerySave(): boolean {
    return this._authService.hasProfile(rbac.profiles.QUERY_READ_OWN);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
  }
}

export type FormError = {
  field: string;
  message: string;
};
